import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Pause } from 'lucide-react';

interface PausedCampaignIndicatorProps {
  isPaused?: boolean;
  pauseReason?: string;
  variant?: 'icon' | 'chip';
  showText?: boolean;
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}

/* eslint-disable max-lines-per-function */
export const PausedCampaignIndicator: React.FC<PausedCampaignIndicatorProps> = ({
  isPaused = false,
  pauseReason,
  variant = 'icon',
  showText = false,
  size = 'default',
  className = ''
}) => {
  if (!isPaused) return null;

  const tooltipText = pauseReason
    ? `This campaign is paused: ${pauseReason}`
    : 'This campaign is currently paused.';

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    default: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    default: 'h-3.5 w-3.5',
    lg: 'h-4 w-4'
  };

  if (variant === 'chip') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant='outline'
              className={`inline-flex items-center gap-1 bg-muted/50 text-muted-foreground border-muted ${sizeClasses[size]} ${className}`}
            >
              <Pause className={iconSizes[size]} />
              {showText && <span>Paused</span>}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={
              'inline-flex items-center justify-center text-muted-foreground ' +
              `hover:text-muted-foreground/80 transition-colors ${className}`
            }
            aria-label='Paused campaign'
          >
            <Pause className={iconSizes[size]} />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
